import { useState, useEffect, useCallback, useRef } from 'react';
import P9Complexity from './P9Complexity';
import P10GoodLesson from './P10GoodLesson';
import P11TraditionalValue from './P11TraditionalValue';
import P12TraditionalLimits from './P12TraditionalLimits';
import P13UnifiedRubric from './P13UnifiedRubric';
import P14ExpertToSample from './P14ExpertToSample';
import P15EvidenceChain from './P15EvidenceChain';
import P16InteractionEvidence from './P16InteractionEvidence';
import P17EvidenceTeacher from './P17EvidenceTeacher';
import P18EvidenceExpert from './P18EvidenceExpert';
import P19EvidenceStudent from './P19EvidenceStudent';
import P20EvidenceManager from './P20EvidenceManager';
import P21ExperienceToModel from './P21ExperienceToModel';
import P22ModelComparison from './P22ModelComparison';
import P23WrapUp from './P23WrapUp';

const TOTAL_SLIDES = 15;

export default function Section2Carousel() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev' | 'initial'>('initial');
  const containerRef = useRef<HTMLDivElement>(null);

  const goNext = useCallback(() => {
    setDirection('next');
    setActiveSlide((prev) => (prev < TOTAL_SLIDES - 1 ? prev + 1 : prev));
  }, []);

  const goPrev = useCallback(() => {
    setDirection('prev');
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goTo = useCallback((i: number) => {
    setDirection(i > activeSlide ? 'next' : i < activeSlide ? 'prev' : 'initial');
    setActiveSlide(i);
  }, [activeSlide]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        goNext();
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev]);

  const slides = [
    <P9Complexity forceVisible key="p9" />,
    <P10GoodLesson forceVisible key="p10" />,
    <P11TraditionalValue forceVisible key="p11" />,
    <P12TraditionalLimits forceVisible key="p12" />,
    <P13UnifiedRubric forceVisible key="p13" />,
    <P14ExpertToSample forceVisible key="p14" />,
    <P15EvidenceChain forceVisible key="p15" />,
    <P16InteractionEvidence forceVisible key="p16" />,
    <P17EvidenceTeacher forceVisible key="p17" />,
    <P18EvidenceExpert forceVisible key="p18" />,
    <P19EvidenceStudent forceVisible key="p19" />,
    <P20EvidenceManager forceVisible key="p20" />,
    <P21ExperienceToModel forceVisible key="p21" />,
    <P22ModelComparison forceVisible key="p22" />,
    <P23WrapUp forceVisible key="p23" />,
  ];

  return (
    <section
      id="section-2"
      ref={containerRef}
      className="relative min-h-screen overflow-hidden bg-background-50"
    >
      {/* 三层背景光效 */}
      <div className="absolute top-1/3 right-[10%] w-[350px] h-[350px] rounded-full bg-primary-100/10 blur-[120px] pointer-events-none animate-orb-float-2" />
      <div className="absolute bottom-1/4 left-[8%] w-[300px] h-[300px] rounded-full bg-accent-100/8 blur-[110px] pointer-events-none animate-orb-float-3" />
      <div className="absolute top-1/2 left-[55%] w-[250px] h-[250px] rounded-full bg-secondary-100/6 blur-[130px] pointer-events-none animate-orb-float-1" />

      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-all duration-600 ${
            activeSlide === i
              ? direction === 'next'
                ? 'animate-slide-in-right z-10 pointer-events-auto'
                : direction === 'prev'
                  ? 'animate-slide-in-left z-10 pointer-events-auto'
                  : 'animate-slide-initial z-10 pointer-events-auto'
              : 'opacity-0 z-0 pointer-events-none scale-[0.98]'
          }`}
        >
          {slide}
        </div>
      ))}

      <button
        onClick={goPrev}
        disabled={activeSlide === 0}
        className={`absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          activeSlide === 0
            ? 'glass border-white/40 text-foreground-950 cursor-default'
            : 'glass border-white/60 text-gold-500 hover:border-gold-300/60 hover:scale-110 active:scale-95'
        }`}
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
      </button>

      <button
        onClick={goNext}
        disabled={activeSlide === TOTAL_SLIDES - 1}
        className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          activeSlide === TOTAL_SLIDES - 1
            ? 'glass border-white/40 text-foreground-950 cursor-default'
            : 'glass border-white/60 text-gold-500 hover:border-gold-300/60 hover:scale-110 active:scale-95'
        }`}
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
      </button>

      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 flex-wrap justify-center px-4">
        {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              activeSlide === i
                ? 'w-6 h-2 bg-gold-500'
                : 'w-2 h-2 bg-background-300/50 hover:bg-gold-300/60 hover:scale-125'
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-6 md:bottom-8 right-6 md:right-10 z-30">
        <span className="font-mono text-xs text-foreground-950 tracking-wider">
          {activeSlide + 1} / {TOTAL_SLIDES}
        </span>
      </div>
    </section>
  );
}